import React, {
  FunctionComponent,
  forwardRef,
  ReactNode,
  PropsWithChildren,
} from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Slide,
  DialogProps,
  SxProps,
  Theme,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { camelCase, filter, includes, map, omit } from 'lodash';
import { Breakpoint } from '@mui/system';
import { SlideProps } from '@mui/material/Slide/Slide';

export const Transition = forwardRef(
  (props: PropsWithChildren<SlideProps>, ref) => {
    return <Slide direction="up" ref={ref} {...props} />;
  }
);

export interface DialogTitleProps {
  id: string;
  children?: ReactNode;
  onClose: (value: unknown) => void;
}

export const DialogTitle: FunctionComponent<
  DialogTitleProps &
    Pick<IPopupDialog, 'isLoading' | 'variant' | 'closeDisabled'>
> = (
  props: DialogTitleProps &
    Pick<IPopupDialog, 'isLoading' | 'variant' | 'closeDisabled'>
) => {
  const { children, onClose, variant, closeDisabled, ...other } = props;

  return (
    <MuiDialogTitle
      sx={{
        m: 0,
        px: 2,
        py: 1.25,
        background: (theme) =>
          variant === 'primary'
            ? theme.palette.primary.main
            : theme.palette.common.red,
        color: (theme) => theme.palette.common.white,
      }}
      {...omit(other, ['isLoading', 'variant'])}
    >
      <Box display="flex" alignItems="center">
        <Box flexGrow={1}>
          <Typography variant="h5">{children}</Typography>
        </Box>
        {onClose ? (
          <Box>
            {/* Color can't signal disabled here (icon is pinned white
              against this colored header), so show a not-allowed cursor
              instead - disabled buttons otherwise disable pointer-events
              entirely, hiding any cursor change. */}
            <IconButton
              aria-label="close"
              onClick={onClose}
              disabled={props.isLoading || closeDisabled}
              sx={{
                '&.Mui-disabled': {
                  pointerEvents: 'auto',
                  cursor: 'not-allowed',
                },
              }}
            >
              <Close
                sx={{
                  color: (theme) => theme.palette.common.white,
                  ':hover': {
                    color: (theme) => theme.palette.common.white,
                  },
                }}
              />
            </IconButton>
          </Box>
        ) : null}
      </Box>
    </MuiDialogTitle>
  );
};

export interface IPrimaryDialogButtons {
  id: string;
  label: string;
  onClick: (val: any) => void;
  disabled?: boolean;
  title?: string;
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  variant?: 'contained' | 'outlined' | 'text';
  loading?: boolean;
  align?: 'left' | 'right';
}

interface IPopupDialog extends Omit<DialogProps, 'open' | 'onClose'> {
  /**
   * Open the dialog
   */
  open: boolean;
  /**
   * Close the dialog
   */
  close: (value: unknown) => void;
  /**
   * Title of the dialog
   */
  title: string;
  /**
   * Content of the dialog
   */
  children: ReactNode;
  /**
   * Save Button Label
   * Defaults to Save
   */
  saveButtonLabel?: string;
  /**
   * Creates a primary Save button
   * Can be modified by passing in an array of button items
   */
  primaryButtons?: IPrimaryDialogButtons[];
  isLoading?: boolean;
  maxWidth?: Breakpoint | false;
  closeButtonLabel?: string;
  hideDialogActions?: boolean;
  variant?: 'primary' | 'error';
  hideCloseButton?: boolean;
  closeButtonClick?: () => void;
  /**
   * Disables the title-bar X and the footer Cancel/Close button without
   * implying "saving" the way `isLoading` does - `isLoading` also puts
   * every primary button into its spinner state (via LoadingButton's
   * `loading` prop), so reusing it to merely block dismissal makes the
   * dialog look permanently stuck in a save. Use this instead when the
   * dialog just needs to be un-dismissable for a while (e.g. a nested
   * editor is open) with no actual async operation in flight.
   */
  closeDisabled?: boolean;
  /**
   * Extra content in the action bar, after the spacer and just before the
   * Close button.
   */
  actionsContent?: ReactNode;
  /**
   * Extra styles merged into the inner content Box.
   */
  contentSx?: SxProps<Theme>;
}

const PopupDialog: FunctionComponent<IPopupDialog> = ({
  open,
  close,
  title,
  children,
  saveButtonLabel = 'Save',
  closeButtonLabel = 'Cancel',
  primaryButtons = [
    {
      id: 'saveButton',
      label: saveButtonLabel,
      onClick: (e) => close(e),
      disabled: false,
      title: saveButtonLabel,
      color: 'primary',
    },
  ],
  isLoading,
  closeDisabled = false,
  maxWidth = 'sm',
  hideDialogActions = false,
  variant = 'primary',
  hideCloseButton = false,
  closeButtonClick,
  actionsContent,
  contentSx,
  ...rest
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Dialog
      TransitionComponent={Transition}
      onClose={(event, reason) => {
        if (closeDisabled) return;
        if (!includes(['backdropClick', 'escapeKeyDown'], reason)) {
          close(event);
        }
      }}
      aria-labelledby={title}
      open={open}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={fullScreen}
      {...rest}
    >
      <DialogTitle
        id={`${camelCase(title)}-id`}
        onClose={close}
        isLoading={isLoading}
        closeDisabled={closeDisabled}
        variant={variant}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ mb: 1, mt: 2, pb: hideDialogActions ? 2 : 0 }}>
        <Box
          sx={[
            { py: 1 },
            ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
          ]}
        >
          {children}
        </Box>
      </DialogContent>
      {!hideDialogActions && (
        <DialogActions sx={{ p: 2 }}>
          {map(
            filter(primaryButtons, (button) => button.align === 'left'),
            (button) => (
              <LoadingButton
                key={button.id}
                autoFocus
                onClick={button.onClick}
                variant={button.variant || 'contained'}
                loading={button.loading ?? isLoading}
                disabled={button.disabled}
                title={button.title}
                color={button.color}
              >
                {button.label}
              </LoadingButton>
            )
          )}
          <Box sx={{ flexGrow: 1 }} />
          {actionsContent}
          {!hideCloseButton && (
            <Button
              autoFocus
              onClick={closeButtonClick ? closeButtonClick : close}
              variant="outlined"
              disabled={isLoading || closeDisabled}
            >
              {closeButtonLabel}
            </Button>
          )}
          {map(
            filter(primaryButtons, (button) => button.align !== 'left'),
            (button) => (
              <LoadingButton
                key={button.id}
                autoFocus
                onClick={button.onClick}
                variant={button.variant || 'contained'}
                loading={button.loading ?? isLoading}
                disabled={button.disabled}
                title={button.title}
                color={button.color}
              >
                {button.label}
              </LoadingButton>
            )
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PopupDialog;
